import { useEffect, useLayoutEffect, useState } from "react";
import "./styles.css";
import { isMobileUserAgent, decodeArticleFromUrl, encodeArticleForUrl } from "../../helpers/helpers";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Sidebar } from "primereact/sidebar";
import ContactMeBlock from "../../components/ContactMeBlock";
import { BreadCrumb } from "primereact/breadcrumb";
import Footer from "../../components/Footer";
import { database, ref, get } from "../../firebase";
import PriceBlock from "../../components/PriceBlock";
import OrderBlock from "../../components/OrderBlock";
import { Button } from "primereact/button";
import Image from "../../components/Image";
import Navigation from "../../components/Navigation";
import SEO from "../../components/SEO";
import { useBasket } from "../../context/BasketContext";

// Map URL collection names to Firebase database paths
const COLLECTION_PATH_MAP = {
    'billion': 'BILLION',
    'cvd': 'CVD',
    'diamonds': 'CVD',
    'airgold': 'airGold',
    'air-gold': 'airGold',
    'harmony': 'harmony',
    'kolorovi': 'kolorovi',
    'reflect': 'reflect',
    'vidrodzhennya': 'vidrodzhennya',
    'luxury': 'luxury',
};

const ALL_COLLECTION_PATHS = ['BILLION', 'CVD', 'airGold', 'harmony', 'kolorovi', 'reflect', 'vidrodzhennya'];

// Special collections that have a different structure
const SPECIAL_COLLECTION_PATHS = {
    'luxury': ['products/firstSection', 'products/secondSection', 'products/thirdSections'],
};

function Product() {
    const { id, collection } = useParams();
    const [contactVisible, setContactVisible] = useState(false);
    const [isOrderVisible, setIsOrderVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [product, setProduct] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [addedToBasket, setAddedToBasket] = useState(false);

    const isMobile = isMobileUserAgent();
    const navigate = useNavigate();
    const { addToBasket, isInBasket } = useBasket();

    useLayoutEffect(() => {
        document.body.style.backgroundColor = "#1D1D1D";
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    });

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });

        // Reset states when navigating to a new product
        setIsLoading(true);
        setNotFound(false);
        setProduct(null);

        const fetchProduct = async () => {
            try {
                // If collection is provided, fetch only that collection
                if (collection) {
                    const collectionKey = collection.toLowerCase();
                    const dbPath = COLLECTION_PATH_MAP[collectionKey];

                    if (dbPath) {
                        // Check if this is a special collection with nested structure
                        const specialPaths = SPECIAL_COLLECTION_PATHS[collectionKey];

                        if (specialPaths) {
                            // Fetch from all nested paths for special collections (like luxury)
                            const allProducts = [];
                            for (const nestedPath of specialPaths) {
                                const nestedRef = ref(database, `/${dbPath}/${nestedPath}`);
                                const nestedSnapshot = await get(nestedRef);
                                if (nestedSnapshot.exists()) {
                                    const products = nestedSnapshot.val()?.filter((item) => item) || [];
                                    allProducts.push(...products);
                                }
                            }

                            const decodedId = decodeArticleFromUrl(id);
                            const foundProduct = allProducts.find((item) => {
                                const articleStr = `${item.article}`;
                                return articleStr === `${id}` || articleStr === decodedId;
                            });

                            if (foundProduct) {
                                setProduct({ ...foundProduct, collection: collection });
                                setIsLoading(false);
                                return;
                            }
                        } else {
                            // Standard collection structure
                            const collectionRef = ref(database, `/${dbPath}/products`);
                            const snapshot = await get(collectionRef);

                            if (snapshot.exists()) {
                                const products = snapshot.val()?.filter((item) => item) || [];
                                const decodedId = decodeArticleFromUrl(id);
                                const foundProduct = products.find((item) => {
                                    const articleStr = `${item.article}`;
                                    // Try matching with URL id first, then with decoded version
                                    return articleStr === `${id}` || articleStr === decodedId;
                                });

                                if (foundProduct) {
                                    setProduct(foundProduct);
                                    setIsLoading(false);
                                    return;
                                }
                            }
                        }
                    }
                }

                // Fallback: fetch all collections if collection not provided or product not found
                const dbRefTotal = ref(database, `/`);
                const snapshot = await get(dbRefTotal);

                if (snapshot.exists()) {
                    const allProducts = ALL_COLLECTION_PATHS
                        .flatMap(path => snapshot.val()?.[path]?.products || [])
                        .filter((item) => item);

                    const decodedId = decodeArticleFromUrl(id);
                    const foundProduct = allProducts.find((item) => {
                        const articleStr = `${item.article}`;
                        // Try matching with URL id first, then with decoded version
                        return articleStr === `${id}` || articleStr === decodedId;
                    });

                    if (foundProduct) {
                        setProduct(foundProduct);
                    } else {
                        setNotFound(true);
                    }
                } else {
                    setNotFound(true);
                }
            } catch (error) {
                console.error(error);
                setNotFound(true);
            } finally {
                setIsLoading(false);
            }
        };

        fetchProduct();
    }, [id, collection]);

    const breadcrumbItems = [
        { label: "Каталог", url: "/catalog" },
        { label: product?.article || "Товар" },
    ];
    const home = { label: "TSATSKY", url: "/" };

    const getInsertDisplay = (insertType) => {
        if (!insertType || insertType === "-") return "Без вставок";
        return insertType;
    };

    // Always use canonical URL with collection for SEO (prevents duplicate content)
    const encodedArticle = encodeArticleForUrl(product?.article || id);
    const canonicalUrl = product?.collection
        ? `/catalog/${product.collection.toLowerCase()}/${encodedArticle}`
        : collection
        ? `/catalog/${collection}/${encodedArticle}`
        : `/catalog/${encodedArticle}`;

    return (
        <div className="bg-white catalog">
            <SEO
                title={product?.name || `Артикул ${id}`}
                description={product?.description || `${product?.name || 'Ювелірний виріб'} від TSATSKY. ${product?.materialType || ''} ${product?.productType || ''}. Артикул: ${id}`}
                url={canonicalUrl}
                image={product?.image}
                type="product"
                product={product}
            />
            <Navigation showBack variant="dark" />

            <div>
                <BreadCrumb className="mt-4" model={breadcrumbItems} home={home} />

                {isLoading ? (
                    <div className="w-full text-center py-12">
                        <i className="pi pi-spin pi-spinner text-4xl text-gray-400" />
                    </div>
                ) : notFound ? (
                    <div className="w-full text-center py-12">
                        <div className="text-2xl text-gray-500 mb-4">
                            Товар не знайдено
                        </div>
                        <Button
                            label="Повернутися до каталогу"
                            onClick={() => navigate("/catalog")}
                            className="bg-gray-500 text-white px-6 py-3"
                        />
                    </div>
                ) : (
                    <article
                        className="flex flex-col lg:flex-row p-4 lg:p-8 gap-8 lg:gap-16"
                        itemScope
                        itemType="https://schema.org/Product"
                    >
                        {/* Product Image */}
                        <div className="w-full lg:w-1/2">
                            <Image
                                highQualitySrc={product?.image}
                                alt={product?.imageAlt || `${product?.name} - ${product?.productType || 'ювелірний виріб'} від TSATSKY`}
                                className="w-full object-cover"
                                itemProp="image"
                            />
                        </div>

                        {/* Product Info */}
                        <div className="w-full lg:w-1/2 flex flex-col">
                            {/* Collection badge */}
                            {product?.collection && (
                                <span className="text-sm text-gray-400 uppercase tracking-wider mb-2">
                                    Колекція {product.collection}
                                </span>
                            )}

                            {/* Product name */}
                            <h1 className="text-2xl lg:text-3xl font-light text-gray-800 mb-2" itemProp="name">
                                {product?.name}
                            </h1>

                            {/* Article */}
                            <span className="text-sm text-gray-400 mb-6">
                                Артикул: <span itemProp="sku">{product?.article}</span>
                            </span>

                            {/* Price */}
                            <div className="mb-6">
                                <PriceBlock
                                    priceStyles="text-2xl lg:text-3xl text-gray-700"
                                    price={product?.price}
                                    oldPrice={product?.oldPrice}
                                />
                            </div>

                            {/* Description */}
                            {product?.description && (
                                <p className="text-gray-600 leading-relaxed mb-8" itemProp="description">
                                    {product.description}
                                </p>
                            )}

                            {/* Product specs */}
                            <div className="border-t border-gray-200 pt-6 mb-8">
                                <h3 className="text-lg font-medium text-gray-700 mb-4">
                                    Характеристики
                                </h3>
                                <div className="grid grid-cols-1 gap-3">
                                    {product?.materialType && (
                                        <div className="flex justify-between py-2 border-b border-gray-100">
                                            <span className="text-gray-500">Матеріал</span>
                                            <span className="text-gray-800">{product.materialType}</span>
                                        </div>
                                    )}
                                    {product?.productType && (
                                        <div className="flex justify-between py-2 border-b border-gray-100">
                                            <span className="text-gray-500">Тип виробу</span>
                                            <span className="text-gray-800">{product.productType}</span>
                                        </div>
                                    )}
                                    {product?.insertType && (
                                        <div className="flex justify-between py-2 border-b border-gray-100">
                                            <span className="text-gray-500">Вставка</span>
                                            <span className="text-gray-800">
                                                {getInsertDisplay(product.insertType)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Buttons */}
                            <div className="flex flex-col lg:flex-row gap-3">
                                <Button
                                    label={
                                        addedToBasket
                                            ? "Додано до кошика"
                                            : isInBasket(product?.article)
                                            ? "У кошику"
                                            : "Додати до кошика"
                                    }
                                    icon={addedToBasket ? "pi pi-check" : "pi pi-shopping-cart"}
                                    onClick={() => {
                                        addToBasket(product);
                                        setAddedToBasket(true);
                                        setTimeout(() => setAddedToBasket(false), 2000);
                                    }}
                                    className={`order-button w-full lg:w-auto font-semibold py-4 px-8 text-lg tracking-wider ${
                                        addedToBasket ? "added-to-basket" : ""
                                    }`}
                                />
                                <Button
                                    label="Замовити зараз"
                                    onClick={() => setIsOrderVisible(true)}
                                    className="order-button w-full lg:w-auto font-semibold py-4 px-8 text-lg tracking-wider"
                                />
                            </div>

                            {/* Delivery Info */}
                            <Link
                                to="/delivery"
                                className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-100 transition-colors group flex items-center gap-3"
                            >
                                <svg className="w-8 h-8 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 42 42" fill="none">
                                    <path d="M18.5391 18.0849V12.2662H14.7887L19.3554 7.69359C19.5739 7.47376 19.8337 7.29932 20.1197 7.1803C20.4058 7.06127 20.7125 7 21.0223 7C21.332 7 21.6387 7.06127 21.9248 7.1803C22.2108 7.29932 22.4706 7.47376 22.6891 7.69359L27.2558 12.2662H23.4769V18.0849H18.5391Z" fill="#DA291C"/>
                                    <path d="M12.2599 27.2531V14.7355L7.69317 19.3081C7.47346 19.5274 7.29913 19.788 7.18018 20.0749C7.06123 20.3618 7 20.6694 7 20.98C7 21.2906 7.06123 21.5982 7.18018 21.8851C7.29913 22.172 7.47346 22.4326 7.69317 22.6519L12.2599 27.2531Z" fill="#DA291C"/>
                                    <path d="M23.4483 29.7338V23.9093H18.5106V29.7338H14.7316L19.2983 34.3064C19.5169 34.5262 19.7766 34.7007 20.0627 34.8197C20.3487 34.9387 20.6554 35 20.9652 35C21.2749 35 21.5817 34.9387 21.8677 34.8197C22.1538 34.7007 22.4135 34.5262 22.632 34.3064L27.1987 29.7338H23.4483Z" fill="#DA291C"/>
                                    <path d="M29.7447 14.7526V27.2702L34.3114 22.6976C34.5322 22.4751 34.7067 22.211 34.8249 21.9206C34.9431 21.6301 35.0026 21.3191 34.9999 21.0055C34.9973 20.6918 34.9325 20.3819 34.8094 20.0935C34.6864 19.8051 34.5074 19.544 34.2828 19.3253L29.7447 14.7526Z" fill="#DA291C"/>
                                </svg>
                                <div className="flex-1">
                                    <span className="text-gray-800 font-medium block">Доставка Новою Поштою</span>
                                    <span className="text-sm text-gray-500">
                                        Безкоштовно від 50 000 грн
                                    </span>
                                </div>
                                <i className="pi pi-arrow-right text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0" />
                            </Link>

                            {/* Other Delivery Options */}
                            <Link
                                to="/delivery"
                                className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-100 transition-colors group flex items-center gap-3"
                            >
                                <div className="w-8 h-8 flex-shrink-0 rounded-full bg-gray-200 flex items-center justify-center">
                                    <i className="pi pi-truck text-gray-600" />
                                </div>
                                <div className="flex-1">
                                    <span className="text-gray-800 font-medium block">Інші методи доставки</span>
                                    <span className="text-sm text-gray-500">
                                        Укрпошта, Meest, Justin тощо
                                    </span>
                                </div>
                                <i className="pi pi-arrow-right text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0" />
                            </Link>
                        </div>
                    </article>
                )}

                <Footer
                    containerClassNames={"bg-darkgray text-harmony"}
                    isDark
                />
            </div>

            <Sidebar
                className={isMobile ? "" : "w-1/2"}
                position="right"
                visible={contactVisible}
                onHide={() => setContactVisible(false)}
                fullScreen={isMobile}
            >
                <ContactMeBlock />
            </Sidebar>

            <Sidebar
                className={isMobile ? "" : "w-1/2"}
                position="right"
                visible={isOrderVisible}
                onHide={() => setIsOrderVisible(false)}
                fullScreen={isMobile}
            >
                <OrderBlock product={product} />
            </Sidebar>
        </div>
    );
}

export default Product;
